import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'The EVM',
    tagline: 'Where deployed bytecode actually executes, one opcode at a time.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what the EVM actually executes (bytecode vs. Solidity source)
//   Q1 - the difference between storage, memory, and calldata
//   Q2 - why an out-of-gas revert still consumes the gas spent so far
export const blocks: StageBlock[] = [
    content('bytecode', () => (
        <StageSection gradientBorder eyebrow="Stage 5 of 8" title="Solidity is a means to an end: EVM bytecode">
            <Prose>
                The Ethereum Virtual Machine never sees Solidity. When a contract is deployed, the compiler turns
                source code into <strong>bytecode</strong>, a sequence of single-byte opcodes (<code>PUSH1</code>,{' '}
                <code>SSTORE</code>, <code>CALL</code>, <code>JUMPI</code>, and so on) that the EVM executes directly.
                That bytecode is what actually lives at a contract's address and what every node re-executes to
                validate a transaction.
            </Prose>
            <Prose>
                This matters for security: the guarantees you're relying on are whatever the compiled bytecode does,
                not whatever the source code appears to say. Compiler bugs, unusual optimizer behavior, and
                low-level tricks (writing raw bytecode, using inline assembly) all operate at this layer, beneath
                Solidity's syntax.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('execution-model', () => (
        <StageSection title="A stack machine with three kinds of memory">
            <Prose>
                The EVM is a stack-based virtual machine: most opcodes pop their inputs off a stack and push their
                result back on. Around that stack, a running contract call has access to three very different
                places to keep data:
            </Prose>
            <FactGrid items={[
                ['Storage', 'Persistent key-value data tied to the contract\'s address. Writing to it (SSTORE) is one of the most gas-expensive operations in the EVM, and it survives between transactions.'],
                ['Memory', 'A temporary, expandable byte array that exists only for the duration of the current call. Cheap to use, wiped when the call ends.'],
                ['Calldata', 'The read-only input bytes of the current call: the encoded function selector and arguments. Cheapest to read from, cannot be modified.'],
            ]} />
        </StageSection>
    )),

    question(1),

    content('calls-and-gas', () => (
        <StageSection title="Calls, reverts, and the gas that doesn't come back">
            <Prose>
                When a contract calls another contract, execution branches into that contract's bytecode with its
                own portion of the remaining gas, its own memory, and access to the callee's storage, but the
                caller's storage stays untouched unless the callee is entered via <code>delegatecall</code>, which
                runs the callee's code against the caller's own storage instead (a pattern that shows up later in
                the platform's upgradeability and proxy challenges).
            </Prose>
            <KeyPoints items={[
                <>A <code>REVERT</code> rolls back every state change made during that call, but the gas already consumed executing up to that point is not returned to the sender.</>,
                <>Running out of gas entirely behaves like a revert with no error data: everything unwinds, and the gas is still spent.</>,
                <>Because reverts are "free" in the sense of not corrupting state, requiring conditions with <code>require</code>-style checks is the normal way contracts defend their own invariants.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
